import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, MessageSquare, Filter, Plus, Search, X, ChefHat, Shield, Sparkles, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { awardPoints, POINT_VALUES } from '@/components/loyalty/PointsHelper';
import { notifyCommentReply, notifyPostLike, notifyNewPostInGroup } from '@/components/notifications/NotificationHelper';
import { safeArray } from '@/components/utils/safeArray';

import CategoryFilter from '@/components/community/CategoryFilter';
import CreatePost from '@/components/community/CreatePost';
import PostCard from '@/components/community/PostCard';
import PostDetail from '@/components/community/PostDetail';
import RecipeCard from '@/components/recipes/RecipeCard';
import RecipeDetail from '@/components/recipes/RecipeDetail';
import CreateRecipe from '@/components/recipes/CreateRecipe';
import AIContentCard from '@/components/community/AIContentCard';
import AIContentDetail from '@/components/community/AIContentDetail';

export default function Community() {
  const [user, setUser] = useState(null);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateRecipe, setShowCreateRecipe] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeSearchQuery, setRecipeSearchQuery] = useState('');
  const [selectedAIContent, setSelectedAIContent] = useState(null);
  const [generatingAIContent, setGeneratingAIContent] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['posts'],
    queryFn: async () => {
      const data = await base44.entities.Post.list('-created_date');
      return safeArray(data);
    },
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: ['all-users'],
    queryFn: async () => {
      const data = await base44.entities.User.list();
      return safeArray(data);
    },
  });

  const { data: recipes = [] } = useQuery({
    queryKey: ['recipes'],
    queryFn: async () => {
      const data = await base44.entities.Recipe.list('-created_date');
      return safeArray(data);
    },
  });

  const { data: recipeFavorites = [] } = useQuery({
    queryKey: ['recipe-favorites', user?.id],
    queryFn: async () => {
      const data = await base44.entities.RecipeFavorite.filter({ user_id: user?.id });
      return safeArray(data);
    },
    enabled: !!user?.id,
  });

  const { data: myPosts = [] } = useQuery({
    queryKey: ['my-posts', user?.id],
    queryFn: async () => {
      const data = await base44.entities.Post.filter({ created_by: user?.email }, '-created_date');
      return safeArray(data);
    },
    enabled: !!user?.id,
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const data = await base44.entities.Group.list('-created_date');
      return safeArray(data);
    },
  });

  const { data: memberships = [] } = useQuery({
    queryKey: ['my-memberships', user?.id],
    queryFn: async () => {
      const data = await base44.entities.GroupMembership.filter({ user_id: user?.id });
      return safeArray(data);
    },
    enabled: !!user?.id,
  });

  const { data: aiContents = [] } = useQuery({
    queryKey: ['ai-contents', selectedCategory],
    queryFn: async () => {
      let data;
      if (selectedCategory === 'all') {
        data = await base44.entities.AIContent.list('-created_date', 10);
      } else {
        data = await base44.entities.AIContent.filter({ category: selectedCategory });
      }
      return safeArray(data);
    },
  });

  const createPostMutation = useMutation({
    mutationFn: (data) => base44.entities.Post.create(data),
    onSuccess: async (newPost) => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      setShowCreatePost(false);
      toast.success('Post creado exitosamente');

      // Award points
      if (user?.id) {
        await awardPoints(user.id, 'post', POINT_VALUES.post, `Publicó: ${newPost.title}`, newPost.id);
      }

      // Notify group members (if post has a category/group)
      if (newPost.category) {
        await notifyNewPostInGroup({
          groupId: newPost.category,
          postTitle: newPost.title,
          authorName: newPost.author_name,
          postId: newPost.id
        });
      }
    },
  });

  const createRecipeMutation = useMutation({
    mutationFn: (data) => base44.entities.Recipe.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      setShowCreateRecipe(false);
      toast.success('Receta creada exitosamente');
    },
  });

  const toggleRecipeFavoriteMutation = useMutation({
    mutationFn: async (recipeId) => {
      const existing = safeArray(recipeFavorites).find(f => f.recipe_id === recipeId);
      if (existing) {
        await base44.entities.RecipeFavorite.delete(existing.id);
        return { action: 'removed' };
      } else {
        await base44.entities.RecipeFavorite.create({
          recipe_id: recipeId,
          user_id: user.id
        });
        return { action: 'added' };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['recipe-favorites'] });
      toast.success(result.action === 'added' ? 'Receta guardada' : 'Receta eliminada de favoritos');
    },
  });

  const joinGroupMutation = useMutation({
    mutationFn: async (groupId) => {
      await base44.entities.GroupMembership.create({
        group_id: groupId,
        user_id: user.id,
        role: 'member'
      });
      const group = safeArray(groups).find(g => g.id === groupId);
      if (group) {
        await base44.entities.Group.update(groupId, {
          member_count: (group.member_count || 0) + 1
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-memberships'] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Te uniste al grupo');
    },
  });

  const markAIContentHelpfulMutation = useMutation({
    mutationFn: async (contentId) => {
      const content = safeArray(aiContents).find(c => c.id === contentId);
      if (content) {
        await base44.entities.AIContent.update(contentId, {
          helpful_count: (content.helpful_count || 0) + 1
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-contents'] });
      toast.success('¡Gracias por tu feedback!');
    },
  });

  const incrementAIContentViewMutation = useMutation({
    mutationFn: async (contentId) => {
      const content = safeArray(aiContents).find(c => c.id === contentId);
      if (content) {
        await base44.entities.AIContent.update(contentId, {
          view_count: (content.view_count || 0) + 1
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-contents'] });
    },
  });

  const filteredPosts = safeArray(posts).filter(post => {
    if (!post || post.status !== 'approved') return false;
    
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    
    const matchesSearch = !searchQuery || 
      post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.content?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const filteredRecipes = safeArray(recipes).filter(recipe => {
    if (recipeSearchQuery) {
      const query = recipeSearchQuery.toLowerCase();
      const matchesTitle = recipe.title?.toLowerCase().includes(query);
      const matchesIngredients = safeArray(recipe.ingredients).some(ing => 
        ing.toLowerCase().includes(query)
      );
      if (!matchesTitle && !matchesIngredients) return false;
    }
    return true;
  });

  const isRecipeFavorite = (recipeId) => {
    return safeArray(recipeFavorites).some(f => f.recipe_id === recipeId);
  };

  const isMemberOf = (groupId) => {
    return safeArray(memberships).some(m => m.group_id === groupId);
  };

  const generateAIContent = async (category) => {
    setGeneratingAIContent(true);
    try {
      const categoryTopics = {
        diabetes: 'diabetes y control de azúcar en sangre',
        hipertension: 'hipertensión y control de sodio',
        alimentacion_familiar: 'alimentación familiar saludable',
        lectura_etiquetas: 'lectura e interpretación de etiquetas nutricionales'
      };

      const topic = categoryTopics[category] || 'alimentación saludable';
      
      const result = await base44.integrations.Core.InvokeLLM({
        prompt: `Genera contenido educativo y útil sobre ${topic} para una comunidad de personas interesadas en alimentación saludable. 

Incluye:
- Un título atractivo (máximo 80 caracteres)
- Contenido informativo, práctico y fácil de entender (entre 150-300 palabras)
- 3-5 etiquetas relevantes

El tono debe ser:
- Educativo pero accesible
- Empático y motivador
- Basado en evidencia científica
- Sin tecnicismos complejos

Tipo de contenido: genera aleatoriamente un tip práctico, un dato interesante, o un artículo breve.`,
        response_json_schema: {
          type: "object",
          properties: {
            title: { type: "string" },
            content: { type: "string" },
            content_type: { 
              type: "string", 
              enum: ["tip", "article", "fact"]
            },
            tags: { 
              type: "array", 
              items: { type: "string" }
            }
          }
        }
      });

      await base44.entities.AIContent.create({
        category: category,
        content_type: result.content_type,
        title: result.title,
        content: result.content,
        tags: result.tags,
        view_count: 0,
        helpful_count: 0
      });

      queryClient.invalidateQueries({ queryKey: ['ai-contents'] });
      toast.success('Contenido generado exitosamente');
    } catch (error) {
      console.error('Error generating AI content:', error);
      toast.error('Error al generar contenido');
    } finally {
      setGeneratingAIContent(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-gray-50 pb-24 overflow-x-hidden">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40 safe-header">
        <div className="max-w-2xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900 text-base sm:text-lg">Comunidad</h1>
                <p className="text-[10px] sm:text-xs text-gray-400">Comparte y aprende</p>
              </div>
            </div>
            <Button onClick={() => setShowCreatePost(true)} size="sm" className="bg-purple-600 hover:bg-purple-700 h-8 px-2 sm:px-3">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Publicar</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <Tabs defaultValue="posts" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4 sm:mb-6">
            <TabsTrigger value="posts">Posts</TabsTrigger>
            <TabsTrigger value="recipes">Recetas</TabsTrigger>
            <TabsTrigger value="groups">Grupos</TabsTrigger>
          </TabsList>

          <TabsContent value="posts" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar posts..."
                className="pl-10 h-12 rounded-xl border-gray-200 bg-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              )}
            </div>

            <CategoryFilter
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />

            {/* AI Content Section */}
            {safeArray(aiContents).length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                    <h3 className="font-semibold text-gray-900">Contenido destacado</h3>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => generateAIContent(selectedCategory === 'all' ? 'alimentacion_familiar' : selectedCategory)}
                    disabled={generatingAIContent}
                    className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                  >
                    {generatingAIContent ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 mr-1" />
                        Generar nuevo
                      </>
                    )}
                  </Button>
                </div>
                <div className="space-y-3">
                  {safeArray(aiContents).slice(0, 2).map((content, index) => (
                    <AIContentCard
                      key={content.id}
                      content={content}
                      onClick={() => {
                        setSelectedAIContent(content);
                        incrementAIContentViewMutation.mutate(content.id);
                      }}
                      onMarkHelpful={() => markAIContentHelpfulMutation.mutate(content.id)}
                      delay={index * 0.05}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              {isLoading ? (
                <div className="text-center py-8">
                  <p className="text-gray-400">Cargando posts...</p>
                </div>
              ) : safeArray(filteredPosts).length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No hay posts para mostrar</p>
                </div>
              ) : (
                <AnimatePresence>
                  {safeArray(filteredPosts).map((post, index) => {
                    const author = safeArray(allUsers).find(u => u.email === post.created_by);
                    return (
                      <PostCard
                        key={post.id}
                        post={post}
                        author={author}
                        onClick={() => setSelectedPost(post)}
                        currentUserId={user?.id}
                      />
                      );
                      })}
                      </AnimatePresence>
                      )}
            </div>
          </TabsContent>

          <TabsContent value="recipes" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={recipeSearchQuery}
                  onChange={(e) => setRecipeSearchQuery(e.target.value)}
                  placeholder="Buscar recetas..."
                  className="pl-10 h-12 rounded-xl border-gray-200 bg-white"
                />
              </div>
              <Button onClick={() => setShowCreateRecipe(true)} size="sm" className="ml-3 bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-1" />
                Nueva
              </Button>
            </div>

            <div className="grid gap-4">
              <AnimatePresence>
                {safeArray(filteredRecipes).map((recipe, index) => (
                  <RecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    isFavorite={isRecipeFavorite(recipe.id)}
                    onToggleFavorite={() => toggleRecipeFavoriteMutation.mutate(recipe.id)}
                    onClick={() => setSelectedRecipe(recipe)}
                    delay={index * 0.05}
                  />
                ))}
              </AnimatePresence>
            </div>

            {safeArray(filteredRecipes).length === 0 && (
              <div className="text-center py-12">
                <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No se encontraron recetas</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="groups" className="space-y-4">
            <div className="grid gap-4">
              {safeArray(groups).map((group) => (
                <Card key={group.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{group.name}</h3>
                      {group.description && (
                        <p className="text-sm text-gray-600 mt-1">{group.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-3">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {group.member_count || 0} miembros
                        </span>
                        {group.group_type && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {group.group_type}
                          </span>
                        )}
                      </div>
                    </div>
                    {isMemberOf(group.id) ? (
                      <Button variant="outline" size="sm" disabled>
                        Miembro
                      </Button>
                    ) : (
                      <Button onClick={() => joinGroupMutation.mutate(group.id)} size="sm">
                        Unirse
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            {safeArray(groups).length === 0 && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No hay grupos disponibles</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {showCreatePost && (
        <CreatePost
          user={user}
          onSave={(data) => createPostMutation.mutate(data)}
          onClose={() => setShowCreatePost(false)}
        />
      )}

      {selectedPost && (
        <PostDetail
          post={selectedPost}
          user={user}
          onClose={() => setSelectedPost(null)}
        />
      )}

      {showCreateRecipe && (
        <CreateRecipe
          user={user}
          onSave={(data) => createRecipeMutation.mutate(data)}
          onClose={() => setShowCreateRecipe(false)}
        />
      )}

      {selectedRecipe && (
        <RecipeDetail
          recipe={selectedRecipe}
          isFavorite={isRecipeFavorite(selectedRecipe.id)}
          onToggleFavorite={() => toggleRecipeFavoriteMutation.mutate(selectedRecipe.id)}
          onClose={() => setSelectedRecipe(null)}
        />
      )}

      {selectedAIContent && (
        <AIContentDetail
          content={selectedAIContent}
          onClose={() => setSelectedAIContent(null)}
          onMarkHelpful={() => markAIContentHelpfulMutation.mutate(selectedAIContent.id)}
        />
      )}
    </div>
  );
}